import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  fetchMemberById, fetchAllDescendants, fetchAncestors,
  buildHierarchy, computeDepths,
} from "../script/rpc";
import { useAuth } from "../App";
import MlmNode    from "./MlmNode"; 
import InfoDrawer from "./InfoDrawer";

const nodeTypes = { mlmNode: MlmNode };

const EDGE_COLORS = [
  "#7c3aed","#0ea5e9","#059669","#d97706","#db2777","#0891b2","#65a30d",
];

const NODE_W = 220;
const NODE_H = 200;

function computeWidth(node) {
  if (!node.children || node.children.length === 0) { node._w = 1; return; }
  node.children.forEach(computeWidth);
  node._w = node.children.reduce((s, c) => s + c._w, 0);
}

function placeNodes(node, depth, left, rfNodes, rfEdges) {
  const cx = (left + node._w / 2) * NODE_W;
  const y  = depth * NODE_H;

  rfNodes.push({
    id: String(node.id),
    type: "mlmNode",
    position: { x: cx - 100, y },
    data: { member: node, depth, isSelected: false, expanded: true, loadingChildren: false, onSelect: () => {}, onExpand: () => {} },
  });

  if (node.parent_id && node.parent_id[0]) {
    const col = EDGE_COLORS[Math.max(0, depth - 1) % EDGE_COLORS.length];
    rfEdges.push({
      id: `e-${node.parent_id[0]}-${node.id}`,
      source: String(node.parent_id[0]),
      target: String(node.id),
      type: "smoothstep",
      style: { stroke: col, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: col, width: 14, height: 14 },
    });
  }

  let childLeft = left;
  (node.children || []).forEach(child => {
    placeNodes(child, depth + 1, childLeft, rfNodes, rfEdges);
    childLeft += child._w;
  });
}

export default function MemberSubtree() {
  const { memberId } = useParams();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [rootMember, setRootMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (!auth || !memberId) return;

    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const member = await fetchMemberById(auth.uid, auth.password, Number(memberId));
        if (!member) throw new Error("Member not found");
        if (!alive) return;
        setRootMember(member);

        const [descendants, ancestors] = await Promise.all([
          fetchAllDescendants(auth.uid, auth.password, member),
          fetchAncestors(auth.uid, auth.password, member),
        ]);
        if (!alive) return;

        const allRecords = [member, ...descendants, ...ancestors];
        const hierarchy = computeDepths(buildHierarchy(allRecords));
        
        const rfNodes = [], rfEdges = [];
        hierarchy.forEach(computeWidth);
        let left = 0;
        hierarchy.forEach(root => {
          placeNodes(root, 0, left, rfNodes, rfEdges);
          left += root._w;
        });

        const withData = rfNodes.map(n => ({
          ...n,
          data: { ...n.data, onSelect: m => setSelectedMember(m) }
        }));

        setNodes(withData);
        setEdges(rfEdges);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [auth, memberId, setNodes, setEdges]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
      <header style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "0 20px", height: 58, flexShrink: 0,
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}>
        <button onClick={() => window.close()} style={{
          padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
          background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer"
        }}>← Close Tab</button>
        
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
            {rootMember ? `${rootMember.name}'s Network` : "Subtree View"}
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>Full Downline Visualization</div>
        </div>
      </header>

      <div style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#7c3aed", animation: "spin 0.8s linear infinite" }}/>
            <p style={{ color: "#64748b", fontSize: 13 }}>Fetching full downline…</p>
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 24 }}>
            <div style={{ fontSize: 40 }}>⚠️</div>
            <h3 style={{ color: "#dc2626", fontSize: 16 }}>Error loading branch</h3>
            <p style={{ color: "#64748b", fontSize: 13 }}>{error}</p>
          </div>
        )}
        {!loading && !error && (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView fitViewOptions={{ padding: 0.15 }}
            minZoom={0.05} maxZoom={2}
            nodesDraggable nodesConnectable={false}
            panOnScroll={true}
            zoomOnPinch={true}
          >
            <Background color="#cbd5e1" gap={20} size={1} style={{ background: "#f1f5f9" }}/>
            <Controls />
            <MiniMap />
          </ReactFlow>
        )}
      </div>
      <InfoDrawer member={selectedMember} onClose={() => setSelectedMember(null)} />
    </div>
  );
}
